import PageLayout from "components/layout/PageLayout";
import { useDeleteHealthPatientMutation, useGetHealthPatientsQuery, useSaveHealthPatientMutation } from "./api/healthPatientsApiSlice";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import EntityFingerprint from "components/commons/EntityFingerprint";
import AvatarMini from "components/commons/AvatarMini";
import { useRef, useState } from "react";
import { FilterMatchMode } from "primereact/api";
import { calculateAgeFromBirthDateEpochSeconds } from "utility/CalculateAge";
import { NavLink } from "react-router-dom";
import HealthPatientForm from "./components/HealthPatientForm";
import { Dialog } from "primereact/dialog";
import { useTranslation } from "react-i18next";

const HealthPatients = () => {

     const [t] = useTranslation(["global", "health"]);

    const dt = useRef(null);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    // API Call
    const { data: patients, isFetching, isSuccess, refetch } = useGetHealthPatientsQuery();

    const idTemplate = (patient) => {
        return <EntityFingerprint guid={patient.guid} />
    }

    const userTemplate = (patient) => {
        return <AvatarMini guid={patient.user.guid} displayName={patient.user.displayName} destination="/dashboard/health/history" />
    }

    const genderTemplate = (patient) => {
        return patient.info.gender && patient.info.gender[0]
    }

    const birthDateTemplate = (patient) => {
        return patient.info.birthDate && calculateAgeFromBirthDateEpochSeconds(patient.info.birthDate)
    }

    const profileTemplate = (patient) => {
        return <NavLink to={"/dashboard/health/profiles"}><button className="btn btn-secondary">{patient.profile.name}</button></NavLink>
    }

    const actionsTemplate = (patient) => {
        return (
            <div>
                <EditPatientDialog patient={patient} />
                <DeletePatientDialog patient={patient} />
            </div>
        )
    }

    let content

    if (isSuccess) {
        content = <>
            <div className="text-end"><NewHealthPatientDialog /></div>
            <DataTable
                dataKey="guid"
                value={patients}
                ref={dt}
                paginator
                rows={25}
                stripedRows
                tableStyle={{ minWidth: '50rem' }}
                rowsPerPageOptions={[5, 10, 25, 50]}
            >
                <Column field="user.displayName" header={t("property.name")} body={userTemplate} sortable filter></Column>
                <Column field="info.gender" header={t("health:entity.patient.props.gender")} body={genderTemplate} sortable></Column>
                <Column field="info.weight" header={t("health:entity.patient.props.weight")} sortable></Column>
                <Column field="info.height" header={t("health:entity.patient.props.height")} sortable></Column>
                <Column field="info.birthDate" header={t("health:entity.patient.props.age")} body={birthDateTemplate} sortable></Column>
                <Column field="profile.name" header={t("health:entity.profile.props.name")} body={profileTemplate} sortable></Column>
                <Column header={t("property.actions")} body={actionsTemplate} />
            </DataTable>


        </>
    }

    return (
        <PageLayout
            pageTitle={t("health:menu.patients_ext")}
            isLoadingContent={isFetching}
            onPageRefresh={refetch}
            bodyContent={content}
        />

    );
}

export default HealthPatients

const NewHealthPatientDialog = () => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);
    const [savePatient, { isLoading }] = useSaveHealthPatientMutation();

    const handleSave = async (payload) => {
        await savePatient({
            payload,
            userGuid: payload.userGuid,
            healthProfileGuid: payload.healthProfileGuid
        });
        setOpenDialog(false);
    };

    return (
        <>
            <button className="btn btn-primary" onClick={() => setOpenDialog(true)}> + {t("action.add")}</button>
            <Dialog header={t("health:actions.new_patient")} visible={openDialog} onHide={() => setOpenDialog(false)} style={{ width: '20vw' }}>
                <HealthPatientForm
                    onSave={handleSave}
                    onCancel={() => setOpenDialog(false)}
                    isEditMode={false}
                />
            </Dialog>
        </>
    );
};

const EditPatientDialog = ({ patient }) => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);
    const [savePatient, { isLoading }] = useSaveHealthPatientMutation();
    const handleSave = async (payload) => {
        await savePatient({
            payload: {
                guid: patient.guid, // essenziale per l'update
                ...payload
            },
            userGuid: patient.user.guid,
            healthProfileGuid: payload.healthProfileGuid
        });
        setOpenDialog(false);
    };
    return (
        <>
            <button className="btn btn-light" onClick={() => setOpenDialog(true)}><i className="fa-solid fa-pen"></i></button>
            <Dialog header={t("health:actions.edit_patient")} visible={openDialog} onHide={() => setOpenDialog(false)} style={{ width: '20vw' }}>
                <HealthPatientForm
                    initialData={{
                        gender: patient.info?.gender,
                        weight: patient.info?.weight,
                        height: patient.info?.height,
                        birthDate: patient.info?.birthDate,
                        healthProfile: patient.profile,
                        userDisplayName: patient.user.displayName
                    }}
                    isEditMode={true}
                    onSave={handleSave}
                    onCancel={() => setOpenDialog(false)}
                />
            </Dialog>
        </>
    );
};

const DeletePatientDialog = ({ patient }) => {
    const [t] = useTranslation(["global", "health"]);
    const [openDialog, setOpenDialog] = useState(false);
    const [deletePatient] = useDeleteHealthPatientMutation()
    return (
        <>
            <button className="btn btn-danger m-1" onClick={() => setOpenDialog(true)}><i className="fa-solid fa-trash"></i></button>
            <Dialog header={t("health:actions.delete_patient")} visible={openDialog} onHide={() => setOpenDialog(false)} style={{ width: '20vw' }}>
                <span>{t("health:helper.patient_delete_confirm")} <b>{patient.user.displayName}</b> ?</span>
                <div className="text-end mt-4">
                    <button className="btn btn-danger mx-2" onClick={() => {
                        deletePatient(patient.user.guid)
                        setOpenDialog(false)
                    }}> {t("action.delete")} </button>
                    <button className="btn btn-secondary" onClick={() => setOpenDialog(false)}> {t("action.cancel")} </button>
                </div>
            </Dialog>
        </>
    );
};